<script lang="ts" setup>
import ApplicationLogo from "@/components/ApplicationLogo.vue";
import NotificationList from "@/components/NotificationList.vue";
import { useSidebarStore } from "@/stores";
import { Link } from "@inertiajs/vue3";
import { ChevronDown, ArrowLeftSquare, LogOut, Settings, User } from "lucide-vue-next";
import { onClickOutside } from "@vueuse/core";
import { ref } from "vue";
import SidebarProvider from "@/components/ui/sidebar/SidebarProvider.vue";
import SidebarContent from "@/components/ui/sidebar/SidebarContent.vue";
import SidebarMenu from "@/components/ui/sidebar/SidebarMenu.vue";

const sidebar = ref(null);

onClickOutside(sidebar, () => {
  sidebarStore.closeMobile();
});

const sidebarStore = useSidebarStore();

function url(str: string) {
  return new URL(str);
}
</script>

<template>
  <div class="relative isolate flex min-h-svh max-lg:flex-col">
    <Head :title="$page.props.title" />

    <NotificationList />

    <TooltipProvider>
      <SidebarProvider>
        <Sidebar collapsible="icon">
          <SidebarHeader class="p-0 flex items-center w-full px-4">
            <div class="border-b h-12 flex items-center w-full">
              <Link
                :href="route('admin.dashboard')"
                class="text-xs focus flex items-center gap-2 text-zinc-500"
              >
                <ArrowLeftSquare class="size-4" /> <span class="group-data-[state=collapsed]:hidden">Back to Application</span>
              </Link>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup v-for="group in ($page.props?.settings as any) ?? []">
              <SidebarGroupLabel class="text-gray-500">{{ group.title }}</SidebarGroupLabel>
              <SidebarMenu>
                <SidebarMenuItem
                  v-for="item in group.children"
                  :key="item.title"
                >
                  <SidebarMenuButton
                    :tooltip="item.title"
                    :as="Link"
                    :href="item.href"

                    :class="{
                      'bg-white border-gray-200': url(item.href).pathname == $page.url,
                      'border-transparent': url(item.href).pathname != $page.url,
                    }"
                  >
                    <Icon :name="item.icon" />
                    <span>{{ item.title }}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>
          <SidebarRail />
        </Sidebar>
        <SidebarInset>
          <header class="flex w-full0 justify-between h-12 px-4 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <div class="border-b flex items-center justify-between h-12 w-full">
              <div class="flex items-center gap-2">
                <SidebarTrigger class="" />
                <Separator
                  orientation="vertical"
                  class="mr-2 h-4"
                />
                <Breadcrumb>
                  <BreadcrumbList>
                    <template v-for="(item, index) in $page.props.breadcrumbs">
                      <BreadcrumbItem class="hidden md:block">
                        <BreadcrumbLink
                          :href="item.href"
                          :as="Link"
                        >
                          {{ item.title }}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator
                        v-if="index != $page.props.breadcrumbs.length - 1"
                        class="hidden md:block"
                      />
                    </template>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <div class="flex items-center gap-4"></div>
            </div>
          </header>
          <div class="flex flex-1 flex-col">
            <div class="p-4 mx-auto max-w-2xl w-full">
              <slot></slot>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  </div>
</template>
